import { useAtomValue } from 'jotai';
import { selectedMergedListingAtom } from '../../../data/atoms';
import {
  BrokerNerdsBarkerCoreEnumsPointOfSale,
  BrokerNerdsBarkerCoreModelsSalesListingSoldVendorPropertiesTradeDeskPos,
  useGetApiEnterpriseInTuneAnalyticsOffersOfferId,
  usePutApiEnterpriseInTuneAnalyticsOffersOfferId,
} from '../../../api';
import classes from '../../NowPricing/NowPricing.styles.tsx.module.css';
import { Badge, Container, Grid, SimpleGrid, Text } from '@mantine/core';
import { BNNumberInput } from '../../../components/NumberInput/NumberInput';
import { useForm } from '@mantine/form';
import { BNButton } from '../../../components/Button/Button';
import { useDidUpdate } from '@mantine/hooks';

export function OfferRule() {
  const selectedListing = useAtomValue(selectedMergedListingAtom);

  let offerId = '';

  if (selectedListing?.vendorProperties?.pointOfSaleId === BrokerNerdsBarkerCoreEnumsPointOfSale.TradeDeskPos) {
    const vendorProperties = selectedListing.vendorProperties as BrokerNerdsBarkerCoreModelsSalesListingSoldVendorPropertiesTradeDeskPos;
    offerId = vendorProperties?.listingExternalId ?? '';

    // Some external IDs aren't actually offer Ids, unless they're an integer, don't assume it's an offer ID
    if (offerId && isNaN(Number(offerId))) {
      offerId = '';
    }
  }

  const {
    data: offerDetails,
    isFetching,
    isError,
  } = useGetApiEnterpriseInTuneAnalyticsOffersOfferId(offerId, {
    query: {
      enabled: !!offerId,
    },
    axios: {
      headers: {
        'x-tenant-id': selectedListing?.tenantId,
      },
    },
  });

  const { mutateAsync: updateOffer, isPending: isSaving } = usePutApiEnterpriseInTuneAnalyticsOffersOfferId({
    axios: {
      headers: {
        'x-tenant-id': selectedListing?.tenantId,
      },
    },
  });

  const disableFormFields = isFetching || isError;

  const form = useForm({
    initialValues: {
      maxAmountOpenInventory: 0,
      ceilingPrice: 0,
      adjustmentFloorValue: 0,
    },
  });

  // Sync form values whenever the fetched offer changes. useDidUpdate (not useEffect) avoids
  // depending on the unstable Mantine `form` instance; the initialValues above cover the mount state.
  useDidUpdate(() => {
    form.setValues({
      maxAmountOpenInventory: offerDetails?.data.maxAmountOpenInventory ?? 0,
      ceilingPrice: offerDetails?.data.ceilingPrice ?? 0,
      adjustmentFloorValue: offerDetails?.data.adjustmentFloorValue ?? 0,
    });
    form.resetDirty();
  }, [offerDetails]);

  if (offerId === '') {
    return null;
  }

  return (
    <Container m={8} mt={0} p={16} pt={32} pb={20} className={classes.wrapper}>
      <Badge radius={0} fz={10} className={`slantBadge ${classes.slantBadge}`}>
        Offer Details
      </Badge>
      <Grid>
        <Grid.Col span={3}>
          <BNNumberInput
            {...form.getInputProps('maxAmountOpenInventory')}
            selectOnFocus
            decimalScale={0}
            fixedDecimalScale
            step={1}
            stepHoldDelay={500}
            stepHoldInterval={100}
            min={0}
            size="xs"
            label="Max Tix"
            disabled={disableFormFields}
          />
        </Grid.Col>
        <Grid.Col span={5}>
          <BNNumberInput
            {...form.getInputProps('ceilingPrice')}
            selectOnFocus
            decimalScale={2}
            fixedDecimalScale
            step={1}
            leftSection={<Text size="xs">$</Text>}
            stepHoldDelay={500}
            stepHoldInterval={100}
            min={0}
            size="xs"
            label="Ceiling"
            disabled={disableFormFields}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <BNNumberInput
            {...form.getInputProps('adjustmentFloorValue')}
            selectOnFocus
            decimalScale={0}
            fixedDecimalScale
            step={1}
            stepHoldDelay={500}
            stepHoldInterval={100}
            min={0}
            size="xs"
            label="Floor"
            suffix={' %'}
            disabled={disableFormFields}
          />
        </Grid.Col>
      </Grid>
      <SimpleGrid mt={16} cols={2} spacing={12} w="100%">
        <BNButton size="xs" fullWidth variant="default" disabled={!form.isDirty() || isSaving} onClick={() => form.reset()}>
          Cancel
        </BNButton>
        <BNButton
          type="submit"
          size="xs"
          fullWidth
          variant="filled"
          disabled={!form.isDirty()}
          loading={isSaving}
          onClick={() =>
            form.onSubmit(async (values) => {
              await updateOffer({
                offerId: offerId,
                data: {
                  inTuneOfferId: offerId,
                  maxAmountOpenInventory: values.maxAmountOpenInventory,
                  ceilingPrice: values.ceilingPrice,
                  adjustmentFloorValue: values.adjustmentFloorValue,
                },
              });
              form.resetDirty();
            })()
          }
        >
          Save
        </BNButton>
      </SimpleGrid>
    </Container>
  );
}
